/**
 * WidgetKPI — single big number + label + optional delta arrow.
 *
 * Executor returns a number or `{ total: number }` for KPI widgets; we
 * accept either shape so the same component works against both the b1adb
 * SQL path (`SUM(...) AS total`) and the mm_bridge tool path (which may
 * return a scalar or a wrapper object).
 */

import type { KpiWidget } from '../types'

interface WidgetKPIProps {
  widget: KpiWidget
  data?: unknown
  error?: string
}

function extractNumber(data: unknown): number | null {
  if (data == null) return null
  if (typeof data === 'number') return Number.isFinite(data) ? data : null
  if (Array.isArray(data)) {
    const first = data[0]
    if (first && typeof first === 'object') {
      const obj = first as Record<string, unknown>
      const val = obj.total ?? obj.value ?? obj[Object.keys(obj)[0]]
      return typeof val === 'number' && Number.isFinite(val) ? val : null
    }
  }
  if (typeof data === 'object') {
    const obj = data as Record<string, unknown>
    const val = obj.total ?? obj.value
    return typeof val === 'number' && Number.isFinite(val) ? val : null
  }
  return null
}

function formatMetric(metric: string, value: number | null): string {
  if (value == null) return '—'
  if (metric.endsWith('_cents')) {
    return `$${(value / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
  }
  if (metric === 'repeat_play_rate') {
    return `${(value * 100).toFixed(1)}%`
  }
  return value.toLocaleString()
}

export function WidgetKPI({ widget, data, error }: WidgetKPIProps) {
  const value = extractNumber(data)
  return (
    <div className="flex h-full flex-col justify-between rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {widget.title}
      </div>
      <div className="mt-2 text-3xl font-bold text-butler-700 dark:text-butler-200">
        {error ? <span className="text-sm text-rose-500">data unavailable</span> : formatMetric(widget.metric, value)}
      </div>
      {error && (
        <div className="mt-1 text-[10px] text-slate-400" title={error}>
          {error}
        </div>
      )}
    </div>
  )
}
